import timeEntryUtils from "../../../shared/utils/timeEntry";
import {
  EasyTimePickerComposablesData,
  EasyTimePickerInputEmits,
  EasyTimePickerInputProps,
  InputRef,
  TimeParts,
} from "../../types/easyTimePicker";
import { nextTick } from "vue";
import { useGetSetHelpers } from "./getSetHelpers";
import { BLUR_DELAY_MS } from "../../constants/easyTimePicker";

export const useFocusEvents = (
  props: EasyTimePickerInputProps,
  emit: EasyTimePickerInputEmits,
  data: EasyTimePickerComposablesData
) => {
  const { setInputValue, getInputRef, getInputValue } = useGetSetHelpers(props, emit, data);

  const triggerInputBlur = (part: TimeParts) => {
    const inputRef = getInputRef(part);
    if (inputRef) dispatchInputEvent(inputRef, "blur");

    data.inputFocuses[part] = false;
    emit("blur");
  };

  const dispatchInputEvent = (inputRef: InputRef, event = "input") => {
    inputRef.value?.dispatchEvent(
      new Event(event, {
        bubbles: true,
        cancelable: true,
      })
    );
  };

  const handleClickOutsideTimeParts = async (part: TimeParts): Promise<void> => {
    data.inputFocuses[part] = false;
    await nextTick();

    const inputValue = getInputValue(part);
    if (inputValue !== "") setInputValue(part, timeEntryUtils.padTimePart(inputValue));
  };

  const focusAndSetCursor = (part: TimeParts, cursorPosition: number) => {
    const inputRef = getInputRef(part);
    if (!inputRef?.value) return;

    inputRef.value.focus();
    data.inputFocuses[part] = true;
    emit("focus");
    setTimeout(() => {
      inputRef.value?.setSelectionRange(cursorPosition, cursorPosition);
    }, BLUR_DELAY_MS);
  };

  const focusAndSetCursorOnNextInput = (currentPart: TimeParts) => {
    const nextInputPart = currentPart === TimeParts.Minutes ? TimeParts.Meridiem : TimeParts.Minutes;

    if (currentPart === TimeParts.Hours) data.inputFocuses[TimeParts.Hours] = false;
    if (currentPart === TimeParts.Minutes && props.useMeridiem) data.inputFocuses[TimeParts.Minutes] = false;
    focusAndSetCursor(nextInputPart, 0);
  };

  return {
    handleClickOutsideTimeParts,
    focusAndSetCursorOnNextInput,
    focusAndSetCursor,
    triggerInputBlur,
    dispatchInputEvent,
  };
};
